import styled from 'styled-components';
import tw from 'tailwind.macro';
import Nodi from '../../images/illustrations/nodi.webp';
import { screens } from '../../styles/constants';
import {
  getLocaleFromPathname,
  URL_DEFAULT_LOCALE,
  URL_LOCALES,
} from '../../utils/i18nUrls';
import { getPreferredLocale } from '../../utils/localePreference';
import {
  ANDROID_PLATFORM,
  appLink,
  IOS_PLATFORM,
} from '../AppPromotionBanner/utils';
import { appUpdateWallMessages } from './messages';

const Container = styled.div`
  ${tw`fixed flex flex-col items-center justify-center text-center bg-white px-medium2`}
  inset: 0;
  z-index: 100;
  gap: var(--size-medium2);
`;

const Image = styled.img`
  ${tw`h-auto`}
  width: 120px;
`;

const Title = styled.h1`
  ${tw`m-0 font-kyrial text-title-xlarge-m leading-title-xlarge-m
    lg:text-title-xlarge lg:leading-title-xlarge`}
`;

const Description = styled.p`
  ${tw`m-0 text-p-medium-m leading-p-medium-m`}
  max-width: 420px;
`;

const StoreLink = styled.a`
  ${tw`no-underline text-white bg-primary-400 rounded-15 font-semibold
    px-medium2 py-medium text-p-medium-m leading-p-medium-m`}

  @media (min-width: ${screens.md}) {
    ${tw`text-p-medium leading-p-medium`}
  }
`;

/**
 * @description the store the running app was installed from. Old versions
 * replace the whole user agent ('@tictactrip/app v1.7.0 (iOS; 18.7.8;
 * iPhone 13)'), and some android ones name the build rather than the system,
 * so anything that is not iOS is treated as android.
 */
const getStoreLink = (userAgent: string): string =>
  /iOS|iPhone|iPad|iPod/i.test(userAgent)
    ? appLink[IOS_PLATFORM]
    : appLink[ANDROID_PLATFORM];

/**
 * @description the language this screen speaks. Everywhere else the language
 * follows the URL alone (see LanguageProvider), but this screen is a dead end
 * inside the app's WebView: no crawler ever sees it, and nobody can reach a
 * translated URL from it, so the phone's language is used when neither the URL
 * nor an earlier explicit choice says anything.
 */
const getWallLocale = (): string => {
  const urlLocale = getLocaleFromPathname(window.location.pathname);

  if (urlLocale) {
    return urlLocale;
  }

  const storedLocale = getPreferredLocale();

  if (URL_LOCALES.includes(storedLocale)) {
    return storedLocale;
  }

  const phoneLocale = window.navigator?.language?.slice(0, 2);

  return URL_LOCALES.includes(phoneLocale) ? phoneLocale : URL_DEFAULT_LOCALE;
};

/**
 * @description full screen shown, in place of the site, to app versions the
 * API no longer serves (see isOutdatedApp). It is deliberately a dead end:
 * every other screen would fail on the first call, so the only way out is the
 * store. Rendered inside the app's WebView only, never on the web.
 */
const AppUpdateWall = () => {
  const locale = getWallLocale();
  // Read straight from the per-locale strings, the format every translation
  // bundle is built from (see translations/util.js): french, served at the
  // unprefixed URL, lives in defaultMessage.
  const t = (message: Record<string, string>) =>
    message[locale] ?? message.defaultMessage;

  return (
    <Container lang={locale} role="dialog">
      <Image alt="" src={Nodi} />

      <Title>{t(appUpdateWallMessages.title)}</Title>

      <Description>{t(appUpdateWallMessages.description)}</Description>

      {/* No target="_blank": the versions this screen is for predate the app's
          onOpenWindow handler, and an iOS WebView opens no window without it,
          which would leave the only button dead. */}
      <StoreLink href={getStoreLink(navigator.userAgent)}>
        {t(appUpdateWallMessages.update)}
      </StoreLink>
    </Container>
  );
};

export default AppUpdateWall;
